import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Keeps the lead-assignment page current when leads change OUTSIDE this browser.
 *
 * WHAT THIS IS FOR
 * ----------------
 * Your own assignments were already instant — useAssignLead does an optimistic
 * setQueryData and invalidates on success. What had no answer was everything
 * else: another owner assigning a list, a bulk move, an import finishing, or an
 * admin fixing ownership directly in the database. Those only surfaced on the
 * next background poll, which is 120s on the unassigned list. Two minutes of
 * looking at a lead that has already moved reads as "the page is broken", and
 * the workaround people learn is to leave and come back.
 *
 * ⚠️ WHY IT IS THROTTLED, NOT LIVE PER-ROW
 * On 2026-09-01 a single afternoon of imports inserted 15,694 leads. A naive
 * `postgres_changes` handler that invalidated per event would have fired ~15,000
 * refetches at every open browser — each one a full unassigned-list query — and
 * taken the page down more effectively than stale data ever did. So events are
 * COALESCED: any number arriving inside the window produce exactly one refresh.
 *
 * The window is deliberately short (2.5s) for the common case of one person
 * assigning a list, and a hard ceiling stops a bulk operation from holding the
 * refresh off forever.
 */

const COALESCE_MS = 2_500;      // burst of events → one refresh
const MAX_DEFER_MS = 10_000;    // never wait longer than this during a long burst

export function useLeadAssignmentRealtime(agencyId?: string | null) {
  const queryClient = useQueryClient();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firstEventAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!agencyId) return;

    let channel: any = null;
    let client: any = null;
    let mounted = true;

    const flush = () => {
      timerRef.current = null;
      firstEventAtRef.current = null;
      if (!mounted) return;
      // The same keys the assign mutation invalidates, so realtime and local
      // mutations converge on identical state.
      for (const key of [
        ['unassignedLeads'],
        ['allLeads'],
        ['agentWorkload'],
        ['agentLeads'],
        ['status-tracking'],
        ['lead-stats'],
      ]) {
        queryClient.invalidateQueries({ queryKey: key });
      }
    };

    const schedule = () => {
      const now = Date.now();
      if (firstEventAtRef.current === null) firstEventAtRef.current = now;

      // A long burst (an import writing thousands of rows) would otherwise keep
      // pushing the timer out and never refresh at all. Force it through.
      if (now - firstEventAtRef.current >= MAX_DEFER_MS) {
        if (timerRef.current) clearTimeout(timerRef.current);
        flush();
        return;
      }
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, COALESCE_MS);
    };

    (async () => {
      try {
        const { getSupabaseClient } = await import('@/app/_module/services/supabaseClient');
        client = await getSupabaseClient();
        if (!mounted) return;

        channel = client
          .channel(`lead-assignment-${agencyId}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'leads',
              // Scoped to this agency so one busy agency cannot wake up every
              // other agency's browsers.
              filter: `agency_id=eq.${agencyId}`,
            },
            () => {
              if (mounted) schedule();
            },
          )
          .subscribe();
      } catch {
        // Realtime is an accelerator, never the source of truth. If the channel
        // cannot be established the existing polls still refresh the page — the
        // user is back to the old behaviour, not a broken one.
      }
    })();

    return () => {
      mounted = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (client && channel) {
        try {
          client.removeChannel(channel);
        } catch {
          /* already torn down */
        }
      }
    };
  }, [agencyId, queryClient]);
}

export default useLeadAssignmentRealtime;
